<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useWindowSize } from '@vueuse/core';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const { t } = useI18n();
const { width } = useWindowSize();

const deferredPrompt = ref(null);
const shouldShowPrompt = ref(false);
const mediaQueryList =
  typeof window !== 'undefined'
    ? window.matchMedia('(display-mode: standalone)')
    : null;
const isStandalone = ref(
  mediaQueryList?.matches || window?.navigator?.standalone || false
);

const isSmallScreen = computed(() => width.value <= 768);

const hasDismissedPrompt = () =>
  localStorage.getItem(LOCAL_STORAGE_KEYS.PWA_INSTALL_PROMPT_DISMISSED) ===
  'true';

const showInstallBanner = computed(() => {
  return shouldShowPrompt.value && isSmallScreen.value && !isStandalone.value;
});

const handleBeforeInstallPrompt = event => {
  event.preventDefault();
  deferredPrompt.value = event;
  if (!hasDismissedPrompt()) {
    shouldShowPrompt.value = true;
  }
};

const handleDisplayModeChange = event => {
  isStandalone.value = event.matches;
  if (event.matches) {
    shouldShowPrompt.value = false;
  }
};

const dismissPrompt = () => {
  shouldShowPrompt.value = false;
  localStorage.setItem(LOCAL_STORAGE_KEYS.PWA_INSTALL_PROMPT_DISMISSED, 'true');
  deferredPrompt.value = null;
};

const installApp = async () => {
  if (!deferredPrompt.value) return;
  deferredPrompt.value.prompt();
  const { outcome } = await deferredPrompt.value.userChoice;
  if (outcome === 'accepted') {
    shouldShowPrompt.value = false;
  }
  deferredPrompt.value = null;
};

onMounted(() => {
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  if (mediaQueryList?.addEventListener) {
    mediaQueryList.addEventListener('change', handleDisplayModeChange);
  } else if (mediaQueryList?.addListener) {
    mediaQueryList.addListener(handleDisplayModeChange);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  if (mediaQueryList?.removeEventListener) {
    mediaQueryList.removeEventListener('change', handleDisplayModeChange);
  } else if (mediaQueryList?.removeListener) {
    mediaQueryList.removeListener(handleDisplayModeChange);
  }
});
</script>

<template>
  <transition name="toast-fade">
    <div
      v-if="showInstallBanner"
      class="fixed bottom-20 left-4 right-4 z-[9991] rounded-2xl border border-n-weak bg-n-solid-1 shadow-lg p-4 flex flex-col gap-3"
    >
      <div>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('mobile.INSTALL_PROMPT.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ t('mobile.INSTALL_PROMPT.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <NextButton
          class="flex-1"
          size="sm"
          :label="t('mobile.INSTALL_PROMPT.INSTALL')"
          icon="i-lucide-download"
          @click="installApp"
        />
        <NextButton
          size="sm"
          ghost
          slate
          :label="t('mobile.INSTALL_PROMPT.DISMISS')"
          @click="dismissPrompt"
        />
      </div>
    </div>
  </transition>
</template>
